/* istanbul ignore file -- @preserve */
import { useCallback, useEffect, useRef } from 'react';
import type { Message } from '../models';
import {
  chatCleared,
  messagesRestored,
  retryMessage,
  selectApprovalRequest,
  selectCurrentToolName,
  selectError,
  selectIsLoading,
  selectMessages,
  selectProgressMessage,
  selectSkipTypewriter,
  selectStreamingStatus,
  selectThreadId,
  sendApproval,
  sendMessage,
  typingCompleted,
  useAppDispatch,
  useAppSelector,
} from '../store';

export function useAgentChatStore() {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectMessages);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const streamingStatus = useAppSelector(selectStreamingStatus);
  const currentToolName = useAppSelector(selectCurrentToolName);
  const progressMessage = useAppSelector(selectProgressMessage);
  const approvalRequest = useAppSelector(selectApprovalRequest);
  const skipTypewriter = useAppSelector(selectSkipTypewriter);
  const threadId = useAppSelector(selectThreadId);

  // Tracks the in-flight streaming thunk so it can be aborted before the
  // displayed thread changes — prevents thread A's stream polluting thread B.
  const activeRequestRef = useRef<{ abort: () => void } | null>(null);

  const trackActiveRequest = useCallback((request: { abort: () => void } & PromiseLike<unknown>) => {
    activeRequestRef.current = request;
    void Promise.resolve(request).finally(() => {
      if (activeRequestRef.current === request) {
        activeRequestRef.current = null;
      }
    });
  }, []);

  const abortActiveRequest = useCallback(() => {
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
  }, []);

  // Abort any in-flight stream when the chat surface unmounts.
  useEffect(() => abortActiveRequest, [abortActiveRequest]);

  const sendMessageWs = useCallback(
    (content: string, companyCode: string) => {
      abortActiveRequest();
      trackActiveRequest(dispatch(sendMessage({ content, companyCode })));
    },
    [dispatch, abortActiveRequest, trackActiveRequest],
  );

  const retry = useCallback(() => {
    abortActiveRequest();
    trackActiveRequest(dispatch(retryMessage()));
  }, [dispatch, abortActiveRequest, trackActiveRequest]);

  const newChat = useCallback(() => {
    abortActiveRequest();
    dispatch(chatCleared());
  }, [dispatch, abortActiveRequest]);

  const switchThread = useCallback(
    (newThreadId: string, msgs: Message[]) => {
      abortActiveRequest();
      dispatch(messagesRestored({ threadId: newThreadId, messages: msgs }));
    },
    [dispatch, abortActiveRequest],
  );

  const approveApproval = useCallback(() => {
    abortActiveRequest();
    trackActiveRequest(dispatch(sendApproval({ label: '承認しました', decision: 'approved' })));
  }, [dispatch, abortActiveRequest, trackActiveRequest]);

  const rejectApproval = useCallback(() => {
    abortActiveRequest();
    trackActiveRequest(dispatch(sendApproval({ label: 'キャンセルしました', decision: 'rejected' })));
  }, [dispatch, abortActiveRequest, trackActiveRequest]);

  const editApproval = useCallback(
    (editMessage: string) => {
      abortActiveRequest();
      trackActiveRequest(dispatch(sendApproval({ label: editMessage, decision: 'edited', editMessage })));
    },
    [dispatch, abortActiveRequest, trackActiveRequest],
  );

  const handleTypingComplete = useCallback(
    (messageId: string) => {
      dispatch(typingCompleted({ messageId }));
    },
    [dispatch],
  );

  return {
    messages,
    isLoading,
    error,
    streamingStatus,
    currentToolName,
    progressMessage,
    approvalRequest,
    skipTypewriter,
    threadId,
    sendMessageWs,
    retry,
    newChat,
    switchThread,
    approveApproval,
    rejectApproval,
    editApproval,
    handleTypingComplete,
  };
}
